import React from 'react';
import {
  Lead,
  type Contact,
  type LeadPipeline,
  type LeadPipelineStage,
} from '../../../../types';
import { isLeadOutcome, outcomeFromStatus, type LeadOutcome } from '../../../../utils/leadPipeline';
import { LeadCategoryOption } from '../../../../components/LeadCategoryPicker';
import type { LeadLostPayload } from '../LeadLostModal';
import type { LeadCustomerPickerHandle } from '../LeadCustomerPicker';
import type { ProjectValue } from '../../utils/projectValue';

/** Lead selbst: Laden/Neuladen, Titel/Kategorie/Phase/Ausgang, Pipelines, Kontakt- und Baustellen-Bearbeitung. */
export function useLeadDetailLead(id: string | undefined) {
  const [lead, setLead] = React.useState<Lead | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [status, setStatus] = React.useState('');
  /** Tab „Notizen“ (Zusammenfassung) – getrennt von lead.notes (Baustellen-/Projektbeschreibung). */
  const [freeNotes, setFreeNotes] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [leadCategories, setLeadCategories] = React.useState<LeadCategoryOption[]>([]);
  const [leadTitleEditing, setLeadTitleEditing] = React.useState(false);
  const [leadTitleDraft, setLeadTitleDraft] = React.useState('');
  const leadTitleInputMobileRef = React.useRef<HTMLInputElement>(null);
  const leadTitleInputDesktopRef = React.useRef<HTMLInputElement>(null);
  const [offerNetSum, setOfferNetSum] = React.useState<number | null>(null);
  const [projectValue, setProjectValue] = React.useState<ProjectValue | null>(null);
  const [pipelines, setPipelines] = React.useState<LeadPipeline[]>([]);
  const [leadAssigneeUserIds, setLeadAssigneeUserIds] = React.useState<string[]>([]);

  const [editingContact, setEditingContact] = React.useState(false);
  const [contactSaveError, setContactSaveError] = React.useState<string | null>(null);
  const customerPickerRef = React.useRef<LeadCustomerPickerHandle>(null);

  const [editingSiteAddress, setEditingSiteAddress] = React.useState(false);
  const [siteAddressInput, setSiteAddressInput] = React.useState('');
  const [siteDescriptionInput, setSiteDescriptionInput] = React.useState('');
  const [siteBestellnummerInput, setSiteBestellnummerInput] = React.useState('');
  const [siteAddressSaveError, setSiteAddressSaveError] = React.useState<string | null>(null);
  const [siteContactDraft, setSiteContactDraft] = React.useState<Contact | null>(null);
  const [displayIdDraft, setDisplayIdDraft] = React.useState('');

  const [showVerlorenModal, setShowVerlorenModal] = React.useState(false);
  const [verlorenError, setVerlorenError] = React.useState<string | null>(null);

  const [categoryChangePending, setCategoryChangePending] = React.useState<{
    categoryKey: string;
    categoryLabel: string;
    pipeline: LeadPipeline;
  } | null>(null);
  const [categoryChangeError, setCategoryChangeError] = React.useState<string | null>(null);

  const applyLeadNetTotal = React.useCallback((data: Lead & { offer_net_total?: number | null }) => {
    const total = data.net_total ?? data.offer_net_total ?? null;
    setOfferNetSum(total != null ? total : null);
    setProjectValue(data.project_value ?? null);
  }, []);

  /**
   * Projektwert nach Positionsänderungen neu vom Server holen (gleiche Kaskade wie Liste/Kanban),
   * statt die Vorgänge im Client aufzusummieren. Entprellt, weil der Editor bei jeder Eingabe meldet.
   */
  const leadIdRef = React.useRef<string | null>(null);
  leadIdRef.current = lead?.id != null ? String(lead.id) : null;
  const projectValueTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleProjectValueRefresh = React.useCallback(() => {
    if (projectValueTimerRef.current) clearTimeout(projectValueTimerRef.current);
    projectValueTimerRef.current = setTimeout(async () => {
      const leadId = leadIdRef.current;
      if (!leadId) return;
      try {
        const res = await fetch('/api/leads/board-totals', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: [leadId] }),
        });
        if (!res.ok || leadIdRef.current !== leadId) return;
        const body = (await res.json()) as {
          totals?: Record<string, number | null>;
          values?: Record<string, ProjectValue | null>;
        };
        if (!body.totals || !(leadId in body.totals)) return;
        setOfferNetSum(body.totals[leadId] ?? null);
        setProjectValue(body.values?.[leadId] ?? null);
      } catch {
        /* ignore — Header behält den letzten Wert */
      }
    }, 800);
  }, []);
  React.useEffect(
    () => () => {
      if (projectValueTimerRef.current) clearTimeout(projectValueTimerRef.current);
    },
    [],
  );

  const reloadLead = React.useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/leads/${encodeURIComponent(id)}`);
      if (!res.ok) return;
      const data = (await res.json()) as Lead & { offer_net_total?: number | null };
      setLead(data);
      const users = (data as { assignee_users?: { user_id: string }[] }).assignee_users ?? [];
      setLeadAssigneeUserIds(users.map((u) => u.user_id));
      setStatus(data.status);
      setFreeNotes(data.notiz?.content ?? '');
      applyLeadNetTotal(data);
    } catch {
      /* ignore */
    }
  }, [id, applyLeadNetTotal]);

  React.useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLead(null);
    setOfferNetSum(null);
    setProjectValue(null);
    setIsLoading(true);

    const numericId = parseInt(id, 10);
    const idLower = id.toLowerCase();

    fetch(`/api/leads/${encodeURIComponent(id)}`)
      .then((res) => {
        if (res.ok) return res.json() as Promise<Lead>;
        throw new Error('Lead not found');
      })
      .then((data: Lead & { offer_net_total?: number | null; assignee_users?: { user_id: string }[] }) => {
        if (cancelled) return;
        setLead(data);
        setLeadAssigneeUserIds((data.assignee_users ?? []).map((u) => u.user_id));
        setStatus(data.status);
        setFreeNotes(data.notiz?.content ?? '');
        applyLeadNetTotal(data);
      })
      .catch(() => {
        // Fallback: fetch all leads and find by id or display_id
        return fetch('/api/leads')
          .then((res) => res.json() as Promise<Lead[]>)
          .then((leads) => {
            if (cancelled) return;
            const found =
              leads.find(
                (l) =>
                  String(l.id) === id ||
                  l.display_id === id ||
                  (l.display_id && l.display_id.toLowerCase() === idLower) ||
                  (!Number.isNaN(numericId) && l.id === numericId)
              ) ?? null;
            if (found) {
              setLead(found);
              setLeadAssigneeUserIds(
                ((found as { assignee_users?: { user_id: string }[] }).assignee_users ?? []).map((u) => u.user_id),
              );
              setStatus(found.status);
              setFreeNotes(found.notiz?.content ?? '');
              applyLeadNetTotal(found);
            } else {
              setLead(null);
              setOfferNetSum(null);
            }
          })
          .catch(() => {
            if (cancelled) return;
            setLead(null);
            setOfferNetSum(null);
          });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, applyLeadNetTotal]);

  React.useEffect(() => {
    setLeadTitleEditing(false);
  }, [lead?.id]);

  React.useEffect(() => {
    if (!leadTitleEditing) return;
    const raf = window.requestAnimationFrame(() => {
      const desktop = window.matchMedia('(min-width: 768px)').matches;
      const el = desktop ? leadTitleInputDesktopRef.current : leadTitleInputMobileRef.current;
      el?.focus();
      el?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, [leadTitleEditing]);

  const startEditingLeadTitle = React.useCallback(() => {
    if (!lead) return;
    setLeadTitleDraft((lead.lead_title || '').trim() || '');
    setLeadTitleEditing(true);
  }, [lead]);

  const cancelLeadTitleEdit = React.useCallback(() => {
    if (!lead) return;
    setLeadTitleDraft(lead.lead_title || '');
    setLeadTitleEditing(false);
  }, [lead]);

  const saveLeadTitleFromEdit = React.useCallback(async () => {
    if (!lead) return;
    const trimmed = leadTitleDraft.trim();
    const prev = (lead.lead_title || '').trim();
    if (!trimmed) {
      setLeadTitleDraft(prev);
      setLeadTitleEditing(false);
      return;
    }
    if (trimmed === prev) {
      setLeadTitleEditing(false);
      return;
    }
    const maxLen = 500;
    const toSave = trimmed.slice(0, maxLen);
    setLeadTitleEditing(false);
    setIsSaving(true);
    try {
      const res = await fetch(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_title: toSave }),
      });
      if (res.ok) {
        setLead((p) => (p ? { ...p, lead_title: toSave } : null));
      } else {
        setLeadTitleDraft(prev);
      }
    } catch {
      setLeadTitleDraft(prev);
    } finally {
      setIsSaving(false);
    }
  }, [lead, leadTitleDraft]);

  React.useEffect(() => {
    fetch('/api/settings/lead-categories', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        setLeadCategories(
          data
            .map((row: { key?: string; id?: string; label?: string; sort_order?: number; color?: string }, i: number) => ({
              key: String(row.key ?? row.id ?? ''),
              label: String(row.label ?? ''),
              sort_order: typeof row.sort_order === 'number' ? row.sort_order : i,
              color: row.color != null ? String(row.color) : undefined,
            }))
            .filter((c) => c.key && c.label),
        );
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch('/api/settings/lead-pipelines')
      .then((r) => (r.ok ? r.json() : null))
      .then((data: LeadPipeline[] | null) => {
        if (cancelled || !Array.isArray(data)) return;
        setPipelines(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /** Pipeline des Projekts: über pipeline_id, sonst über Kategorie, sonst Standard-Pipeline. */
  const leadPipeline = React.useMemo<LeadPipeline | null>(() => {
    if (pipelines.length === 0) return null;
    const byId = lead?.pipeline_id ? pipelines.find((p) => p.id === lead.pipeline_id) : null;
    if (byId) return byId;
    const byCategory = lead?.category_key
      ? pipelines.find((p) => p.category_keys.includes(lead.category_key as string))
      : null;
    if (byCategory) return byCategory;
    return pipelines.find((p) => p.is_default) ?? pipelines[0] ?? null;
  }, [pipelines, lead?.pipeline_id, lead?.category_key]);

  const leadPipelineStages = React.useMemo<LeadPipelineStage[]>(
    () => (leadPipeline ? [...leadPipeline.stages].sort((a, b) => a.sort_order - b.sort_order) : []),
    [leadPipeline],
  );

  /** Ausgang des Projekts — bevorzugt `outcome`; fällt auf Status-String zurück (inkl. Legacy „Abgeschlossen“). */
  const leadOutcome = React.useMemo<LeadOutcome>(() => {
    if (!lead) return 'open';
    return isLeadOutcome(lead.outcome) ? lead.outcome : outcomeFromStatus(lead.status ?? status);
  }, [lead, status]);
  const isLeadWon = leadOutcome === 'won';
  const isLeadLost = leadOutcome === 'lost';
  const terminalStatusLabel = isLeadWon ? 'Gewonnen' : isLeadLost ? 'Verloren' : null;

  const applyLeadPipelinePatchResult = React.useCallback(
    (result: { status?: string; stage_id?: string | null; outcome?: LeadOutcome }) => {
      setLead((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(result.status !== undefined ? { status: result.status } : {}),
          ...(result.stage_id !== undefined ? { stage_id: result.stage_id } : {}),
          ...(result.outcome !== undefined ? { outcome: result.outcome } : {}),
        };
      });
      if (result.status !== undefined) setStatus(result.status);
    },
    [],
  );

  const saveLeadAssignees = React.useCallback(
    async (userIds: string[]): Promise<boolean> => {
      if (!lead) return false;
      try {
        const res = await fetch(`/api/leads/${lead.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assignee_user_ids: userIds }),
        });
        return res.ok;
      } catch {
        return false;
      }
    },
    [lead],
  );

  const resolvePipelineForCategoryKey = React.useCallback(
    (categoryKey: string | null | undefined): LeadPipeline | null => {
      if (pipelines.length === 0) return null;
      const def = pipelines.find((p) => p.is_default) ?? pipelines[0] ?? null;
      const key = categoryKey != null ? String(categoryKey).trim() : '';
      if (!key) return def;
      return pipelines.find((p) => p.category_keys.includes(key)) ?? def;
    },
    [pipelines],
  );

  const applyLeadCategoryChange = React.useCallback(
    async (nextKey: string, stageId?: string) => {
      if (!lead) return false;
      setIsSaving(true);
      setCategoryChangeError(null);
      try {
        const body: { category: string | null; stage_id?: string; outcome?: LeadOutcome } = {
          category: nextKey || null,
        };
        if (stageId) {
          body.stage_id = stageId;
          // Keep terminal outcome when only remapping into another pipeline.
          const outcome = isLeadOutcome(lead.outcome)
            ? lead.outcome
            : outcomeFromStatus(lead.status ?? null);
          body.outcome = outcome;
        }
        const res = await fetch(`/api/leads/${lead.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (!res.ok) {
          setCategoryChangeError(data.error || `Speichern fehlgeschlagen (${res.status})`);
          return false;
        }
        await reloadLead();
        setCategoryChangePending(null);
        return true;
      } finally {
        setIsSaving(false);
      }
    },
    [lead, reloadLead],
  );

  const handleLeadCategoryChange = React.useCallback(
    (nextKey: string) => {
      if (!lead) return;
      const prevKey = lead.category_key ?? '';
      if (nextKey === prevKey) return;

      const currentPipelineId =
        leadPipeline?.id ??
        resolvePipelineForCategoryKey(prevKey || null)?.id ??
        null;
      const nextPipeline = resolvePipelineForCategoryKey(nextKey || null);

      // Andere Pipeline → Phase manuell wählen (auch wenn aktuelle Pipeline noch unbekannt ist).
      if (nextPipeline && nextPipeline.id !== currentPipelineId) {
        const categoryLabel =
          leadCategories.find((c) => c.key === nextKey)?.label ?? nextKey;
        setCategoryChangeError(null);
        setCategoryChangePending({
          categoryKey: nextKey,
          categoryLabel,
          pipeline: nextPipeline,
        });
        return;
      }

      void applyLeadCategoryChange(nextKey);
    },
    [
      lead,
      leadPipeline,
      leadCategories,
      resolvePipelineForCategoryKey,
      applyLeadCategoryChange,
    ],
  );

  /** Projekt als „Gewonnen“ markieren; Phase (stage_id) bleibt erhalten. */
  const handleGewonnen = React.useCallback(async () => {
    if (!lead) return;
    setIsSaving(true);
    try {
      const res = await fetch(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: 'won',
          stage_id: lead.stage_id != null ? String(lead.stage_id) : undefined,
        }),
      });
      if (res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          status?: string;
          stage_id?: string | null;
          outcome?: LeadOutcome;
        };
        applyLeadPipelinePatchResult(body);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  }, [lead, applyLeadPipelinePatchResult]);

  /** Terminales Projekt (Gewonnen/Verloren) wieder öffnen — Server remapped auf gespeicherte oder erste Phase. */
  const handleReopenLead = React.useCallback(async () => {
    if (!lead) return;
    setIsSaving(true);
    try {
      const res = await fetch(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome: 'open' }),
      });
      if (res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          status?: string;
          stage_id?: string | null;
          outcome?: LeadOutcome;
        };
        applyLeadPipelinePatchResult(body);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  }, [lead, applyLeadPipelinePatchResult]);

  /** Klick auf eine Phase im Stepper: setzt stage_id und öffnet das Projekt (falls es terminal war). */
  const handleStageClick = React.useCallback(
    async (stageId: string) => {
      if (!lead) return;
      setIsSaving(true);
      try {
        const res = await fetch(`/api/leads/${lead.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stage_id: stageId, outcome: 'open' }),
        });
        if (res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            status?: string;
            stage_id?: string | null;
            outcome?: LeadOutcome;
          };
          applyLeadPipelinePatchResult(body);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsSaving(false);
      }
    },
    [lead, applyLeadPipelinePatchResult],
  );

  const openVerlorenModal = React.useCallback(() => {
    setVerlorenError(null);
    setShowVerlorenModal(true);
  }, []);

  const handleVerlorenSave = React.useCallback(
    async (payload: LeadLostPayload) => {
      if (!lead) return;
      setVerlorenError(null);
      setIsSaving(true);
      try {
        const res = await fetch(`/api/leads/${lead.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            outcome: 'lost',
            status: 'Verloren',
            loss_reason: payload.loss_reason,
            loss_comment: payload.loss_comment,
            lost_at: payload.lost_at,
          }),
        });
        if (!res.ok) {
          const text = await res.text();
          let msg = text || `Fehler ${res.status}`;
          try {
            const j = JSON.parse(text) as { error?: string };
            if (j?.error) msg = j.error;
          } catch {
            /* keep msg */
          }
          setVerlorenError(msg);
          return;
        }
        const body = (await res.json().catch(() => ({}))) as {
          status?: string;
          stage_id?: string | null;
          outcome?: LeadOutcome;
          lost_at?: string;
          lost_by?: string;
          lost_by_user_id?: string | null;
        };
        setStatus(body.status ?? 'Verloren');
        setLead((prev) =>
          prev
            ? {
                ...prev,
                status: body.status ?? 'Verloren',
                outcome: body.outcome ?? 'lost',
                stage_id: body.stage_id !== undefined ? body.stage_id : prev.stage_id,
                loss_reason: payload.loss_reason,
                loss_comment: payload.loss_comment,
                lost_at: body.lost_at ?? payload.lost_at,
                lost_by: body.lost_by,
                lost_by_user_id: body.lost_by_user_id,
              }
            : null
        );
        setShowVerlorenModal(false);
      } catch (err) {
        console.error(err);
        setVerlorenError(err instanceof Error ? err.message : 'Fehler beim Speichern');
      } finally {
        setIsSaving(false);
      }
    },
    [lead],
  );

  const startEditContact = React.useCallback(() => {
    setContactSaveError(null);
    setEditingContact(true);
  }, []);

  const cancelEditContact = React.useCallback(() => {
    setContactSaveError(null);
    setEditingContact(false);
  }, []);

  React.useEffect(() => {
    if (lead && !editingSiteAddress) {
      setSiteAddressInput(lead.baustelle?.address ?? lead.site_address ?? '');
    }
  }, [lead?.id, lead?.site_address, lead?.baustelle?.address, editingSiteAddress]);

  const startEditSiteAddress = React.useCallback(() => {
    if (!lead) return;
    setSiteAddressSaveError(null);
    setSiteAddressInput(lead.baustelle?.address ?? lead.site_address ?? '');
    setSiteDescriptionInput(lead.baustelle?.description ?? lead.notes ?? '');
    setSiteBestellnummerInput(lead.baustelle?.bestellnummer ?? '');
    setSiteContactDraft(lead.baustelle?.contact ?? null);
    setDisplayIdDraft(String(lead.display_id ?? '').trim());
    setEditingSiteAddress(true);
  }, [lead]);

  const cancelEditSiteAddress = React.useCallback(() => {
    if (lead) setSiteAddressInput(lead.baustelle?.address ?? lead.site_address ?? '');
    if (lead) setSiteDescriptionInput(lead.baustelle?.description ?? lead.notes ?? '');
    if (lead) setSiteBestellnummerInput(lead.baustelle?.bestellnummer ?? '');
    setSiteContactDraft(lead?.baustelle?.contact ?? null);
    setDisplayIdDraft(String(lead?.display_id ?? '').trim());
    setSiteAddressSaveError(null);
    setEditingSiteAddress(false);
  }, [lead]);

  const saveSiteAddress = React.useCallback(async () => {
    if (!lead) return;
    const patchId = encodeURIComponent(String(lead.id ?? id ?? ''));
    if (!patchId) {
      setSiteAddressSaveError('Lead-ID fehlt – Seite neu laden.');
      return;
    }
    const canEditProjectNumber = lead.numbering_manual_edit_enabled !== false;
    const displayIdTrimmed = displayIdDraft.trim();
    const prevDisplayId = String(lead.display_id ?? '').trim();
    if (canEditProjectNumber && !displayIdTrimmed) {
      setSiteAddressSaveError('Projektnummer darf nicht leer sein.');
      return;
    }
    setSiteAddressSaveError(null);
    setIsSaving(true);
    try {
      const trimmed = siteAddressInput.trim();
      const descriptionTrimmed = siteDescriptionInput.trim();
      const bestellnummerTrimmed = siteBestellnummerInput.trim();
      const body: Record<string, unknown> = {
        baustelle: {
          address: trimmed ? trimmed : null,
          description: descriptionTrimmed ? descriptionTrimmed : null,
          bestellnummer: bestellnummerTrimmed ? bestellnummerTrimmed : null,
          site_contact_id: siteContactDraft?.id != null ? String(siteContactDraft.id) : null,
        },
      };
      if (canEditProjectNumber && displayIdTrimmed !== prevDisplayId) {
        body.display_id = displayIdTrimmed;
      }
      const res = await fetch(`/api/leads/${patchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `Speichern fehlgeschlagen (${res.status}).`;
        try {
          const errJson = (await res.json()) as { error?: string };
          if (errJson?.error) msg = errJson.error;
        } catch {
          /* ignore */
        }
        setSiteAddressSaveError(msg);
        return;
      }
      setLead((prev) => {
        if (!prev) return null;
        const site = trimmed || '';
        const listAddr = site || (prev.contact?.address ?? '').trim() || prev.address || '';
        return {
          ...prev,
          display_id:
            canEditProjectNumber && displayIdTrimmed ? displayIdTrimmed : prev.display_id,
          site_address: trimmed || null,
          address: listAddr,
          notes: descriptionTrimmed,
          baustelle: {
            ...prev.baustelle,
            lead_id: prev.id,
            address: trimmed || null,
            description: descriptionTrimmed || null,
            bestellnummer: bestellnummerTrimmed || null,
            site_contact_id: siteContactDraft?.id != null ? String(siteContactDraft.id) : null,
            contact: siteContactDraft,
          },
        };
      });
      setEditingSiteAddress(false);
    } catch (e) {
      console.error(e);
      setSiteAddressSaveError(e instanceof Error ? e.message : 'Netzwerkfehler beim Speichern.');
    } finally {
      setIsSaving(false);
    }
  }, [
    lead,
    id,
    siteAddressInput,
    siteDescriptionInput,
    siteBestellnummerInput,
    siteContactDraft,
    displayIdDraft,
  ]);

  const saveContact = React.useCallback(async () => {
    if (!lead) return;
    const patchId = encodeURIComponent(String(lead.id ?? id ?? ''));
    if (!patchId) {
      setContactSaveError('Lead-ID fehlt – Seite neu laden.');
      return;
    }
    setContactSaveError(null);
    setIsSaving(true);
    try {
      const resolved = await customerPickerRef.current?.resolveContactId();
      if (!resolved?.ok) {
        return;
      }
      if (String(lead.contact_id ?? lead.contact?.id ?? '') === resolved.contactId) {
        setEditingContact(false);
        return;
      }
      const res = await fetch(`/api/leads/${patchId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_id: resolved.contactId }),
      });
      if (!res.ok) {
        let msg = `Speichern fehlgeschlagen (${res.status}).`;
        try {
          const errJson = (await res.json()) as { error?: string };
          if (errJson?.error) msg = errJson.error;
        } catch {
          /* ignore */
        }
        setContactSaveError(msg);
        return;
      }
      await reloadLead();
      setEditingContact(false);
    } catch (e) {
      console.error(e);
      setContactSaveError(e instanceof Error ? e.message : 'Netzwerkfehler beim Speichern.');
    } finally {
      setIsSaving(false);
    }
  }, [lead, id, reloadLead]);

  return {
    lead,
    setLead,
    isLoading,
    status,
    freeNotes,
    isSaving,
    setIsSaving,
    leadCategories,
    leadTitleEditing,
    leadTitleDraft,
    setLeadTitleDraft,
    leadTitleInputMobileRef,
    leadTitleInputDesktopRef,
    startEditingLeadTitle,
    cancelLeadTitleEdit,
    saveLeadTitleFromEdit,
    displayIdDraft,
    setDisplayIdDraft,
    offerNetSum,
    projectValue,
    scheduleProjectValueRefresh,
    reloadLead,
    pipelines,
    leadPipeline,
    leadPipelineStages,
    leadOutcome,
    isLeadWon,
    isLeadLost,
    terminalStatusLabel,
    applyLeadPipelinePatchResult,
    leadAssigneeUserIds,
    setLeadAssigneeUserIds,
    saveLeadAssignees,
    resolvePipelineForCategoryKey,
    applyLeadCategoryChange,
    handleLeadCategoryChange,
    handleGewonnen,
    handleReopenLead,
    handleStageClick,
    showVerlorenModal,
    setShowVerlorenModal,
    verlorenError,
    setVerlorenError,
    openVerlorenModal,
    handleVerlorenSave,
    categoryChangePending,
    setCategoryChangePending,
    categoryChangeError,
    setCategoryChangeError,
    editingContact,
    contactSaveError,
    customerPickerRef,
    startEditContact,
    cancelEditContact,
    saveContact,
    editingSiteAddress,
    siteAddressInput,
    setSiteAddressInput,
    siteDescriptionInput,
    setSiteDescriptionInput,
    siteBestellnummerInput,
    setSiteBestellnummerInput,
    siteAddressSaveError,
    setSiteAddressSaveError,
    siteContactDraft,
    setSiteContactDraft,
    startEditSiteAddress,
    cancelEditSiteAddress,
    saveSiteAddress,
  };
}

export type UseLeadDetailLeadResult = ReturnType<typeof useLeadDetailLead>;
